/**
 * StepRequestsDebug Component - Debug Version
 *
 * Shows all tool calls with full debug information:
 * - All tool calls with complete raw data
 * - System prompts
 * - Model requests
 * - Full response information
 *
 * Uses the reactive GraphStore pattern:
 * - useNodes(step.model_requests) for reactive access to model requests
 * - No manual DAG reconstruction needed
 */

import React from 'react';
import type { Step } from '@sourcepilot/generated/chat/api/step';
import { ModelRequestResponse } from '@sourcepilot/ui/framework/ui/molecules/ModelRequestResponse';
import { useNodes } from '@sourcepilot/framework/relationship-manager';

interface StepRequestsDebugProps {
  step: Step;
}

export function StepRequestsDebug({ step }: StepRequestsDebugProps) {
  // REACTIVE: Get model requests from GraphStore using standalone hook
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  // Apply client-side ordering by order_index (memoized internally)
  const sortedModelRequests = useNodes(step.model_requests).orderBy('order_index').value();

  if (sortedModelRequests.length === 0) {
    return null; // No responses to render
  }

  return (
    <div className="flex flex-col space-y-2">
      {sortedModelRequests.map((modelRequest) => (
        <ModelRequestResponse
          key={modelRequest.id}
          modelRequest={modelRequest}
          renderConfig={{
            showAllToolCalls: true,
            showSysPrompts: true,
            showModelRequests: true
          }}
        />
      ))}
    </div>
  );
}
